import { type PropsWithChildren } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { formatDate, formatPercentage } from "@features/mmm/utils/formatters";
import { type DesignElement } from "@hooks/use-design-element-manager";
import { formatCurrency } from "@north-beam/nb-common/formatting";

import { type BudgetMixChartDatum } from "./segmented-chart";

export const BarTooltip = ({
  children,
  designElement,
  breakdownValue,
  isHistorical,
  historicalPercent,
  spend,
  percent,
  historicalBudgetMixData,
  dateRange,
}: PropsWithChildren<{
  designElement: DesignElement;
  breakdownValue: string;
  isHistorical: boolean;
  historicalPercent: number;
  spend: number;
  percent: number;
  historicalBudgetMixData?: BudgetMixChartDatum;
  dateRange: { startDate: string; endDate: string };
}>) => {
  const { user } = useUser();
  const isPositiveTrend = percent >= historicalPercent;
  const isSpendEqual = spend === historicalBudgetMixData?.spend;
  const diffPercentage = Math.abs(historicalPercent - percent);
  const diffSpend = spend - (historicalBudgetMixData?.spend ?? 0);

  return (
    <GenericTooltip
      placement="right"
      mouseEnterDelay={1}
      content={
        <div className="flex flex-col gap-1.5 min-w-[280px]">
          <div className="flex items-center justify-between gap-10">
            <div className="flex items-center gap-2">
              <div>{designElement.getComponent({ size: "1.5em" })}</div>
              <div className="text-v2-primary font-semibold">
                {breakdownValue}
              </div>
            </div>
            {!isHistorical && (
              <div className="text-2xs font-semibold py-1 px-1.5 flex items-center gap-0.5 rounded-full text-cosmic bg-cosmic-4">
                {diffSpend > 0 ? "+" : "-"}
                {formatCurrency(Math.abs(diffSpend), {
                  code: user.currencies.currentCurrency,
                  precision: "approximate",
                })}
                <span> | </span>
                <i
                  className={clsx(
                    "fa-regular",
                    isSpendEqual
                      ? "fa-arrows-left-right"
                      : isPositiveTrend
                      ? "fa-arrow-up"
                      : "fa-arrow-down",
                  )}
                />
                {formatPercentage(diffPercentage)}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-3">
              <div
                className="h-3 w-3 rounded"
                style={{ backgroundColor: designElement.iconColor }}
              ></div>
              <div>This mix</div>
            </div>
            <div className="font-semibold text-interstellar">
              {formatCurrency(spend, {
                code: user.currencies.currentCurrency,
                precision: "exact",
              })}
            </div>
          </div>
          {historicalBudgetMixData && !isHistorical && (
            <div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 relative">
                  <div className="w-3 ml-1"></div>
                  <div>Prior period</div>
                </div>
                <div className="font-semibold text-interstellar">
                  {formatCurrency(historicalBudgetMixData.spend, {
                    code: user.currencies.currentCurrency,
                    precision: "exact",
                  })}
                </div>
              </div>
              <span className="text-gray+2 text-2xs ml-6">
                {`${formatDate(dateRange.startDate)} - ${formatDate(
                  dateRange.endDate,
                )}`}
              </span>
            </div>
          )}
        </div>
      }
      noInfoCircle
    >
      {children}
    </GenericTooltip>
  );
};
